import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  List,
  ListItem,
  ListItemText,
  Radio, 
  RadioGroup, 
  FormControlLabel, 
  FormControl, 
  FormLabel,
  Button,
  CircularProgress,
  Divider,
  Alert,
  Card,
  CardContent,
  CssBaseline,
  GlobalStyles,
  TextField
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { surveyDataService } from '../services/surveyDataService';

interface AnswerItem {
  text: string;
  type: 'positive' | 'negative' | 'neutral';
}

interface Survey {
  id: string;
  title: string;
  answers: AnswerItem[];
  createdAt: number;
  tasks?: string[];
}

interface SurveyResponse {
  surveyId: string;
  questionTitle: string;
  selectedAnswer: string;
  answerType: string;
  taskId: string;
  createdAt: number;
}


const PublicSurvey: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [responses, setResponses] = useState<{ [key: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    const fetchSurveys = async () => {
      if (!taskId) {
        setError('Görev bilgisi bulunamadı.');
        setLoading(false);
        return;
      }

      try {
        // Anonim QR akışı: QR URL yalnız taskId taşır. Önce merkezi resolver ile
        // companyId çözülür, sonra X-Company-Id ile o şirketin anketleri getirilir.
        const cid = await surveyDataService.resolveCompany(taskId);
        if (!cid) {
          // Bu göreve bağlı anket/şirket eşlemesi yok → "henüz anket yok" ekranı.
          setSurveys([]);
          setLoading(false);
          return;
        }
        setCompanyId(cid);

        const list = await surveyDataService.getPublicSurveysByTask(taskId, cid);
        setSurveys(list);
        setLoading(false);
      } catch (error) {
        console.error('Anketler yüklenirken hata:', error);
        setError('Anketler yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
        setLoading(false);
      }
    };

    fetchSurveys();
  }, [taskId]);

  const handleResponseChange = (surveyId: string, answer: string) => {
    setResponses(prev => ({
      ...prev,
      [surveyId]: answer
    }));
  };

  const handleFeedbackChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFeedback(event.target.value.slice(0, 200));
  };

  const handleSubmit = async () => {
    if (!taskId || !companyId) return;
    
    // Tüm anketlerin yanıtlandığını kontrol et
    const allAnswered = surveys.every(survey => responses[survey.id]);
    
    if (!allAnswered) {
      setError('Lütfen tüm soruları cevaplayınız.');
      return;
    }
    
    setSubmitting(true);
    setError(null);

    try {
      // Cevap verilerini hazırla (anahtar = survey.id = SQL anket id'si)
      const answers: { [key: string]: { questionTitle: string; selectedAnswer: string; answerType: string } } = {};
      
      for (const survey of surveys) {
        const selectedAnswer = survey.answers.find(a => a.text === responses[survey.id]);
        
        if (!selectedAnswer) {
          throw new Error(`${survey.title} sorusu için seçilen cevap bulunamadı.`);
        }
        
        answers[survey.id] = {
          questionTitle: survey.title,
          selectedAnswer: selectedAnswer.text,
          answerType: selectedAnswer.type
        };
      }

      // Anonim yanıtı SQL'e gönder (X-Company-Id explicit).
      await surveyDataService.submitPublicSurvey(taskId, companyId, {
        answers,
        feedback: feedback.trim() || null
      });
      
      setSubmitted(true);
    } catch (error) {
      console.error('Cevaplar gönderilirken hata:', error);
      setError('Cevaplarınız gönderilirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <GlobalStyles styles={{
          'html, body': {
            height: '100%',
            overflow: 'auto'
          }
        }} />
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '100vh'
        }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <GlobalStyles styles={{
          'html, body': {
            height: '100%',
            overflow: 'auto'
          }
        }} />
        <Box sx={{ 
          p: 4, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '100vh'
        }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </>
    );
  }

  if (surveys.length === 0) {
    return (
      <>
        <GlobalStyles styles={{
          'html, body': {
            height: '100%',
            overflow: 'auto'
          }
        }} />
        <Box sx={{ 
          p: 4, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '100vh'
        }}>
          <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 'sm', width: '100%' }}>
            <Typography variant="h5" component="h1" gutterBottom>
              Memnuniyet Anketi
            </Typography>
            <Box sx={{ my: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
                Bu görev için henüz anket tanımlanmamış.
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Görev ID: {taskId}
              </Typography>
            </Box>
            <Box sx={{ mt: 4 }}>
              <Typography variant="body2" color="text.secondary">
                © {new Date().getFullYear()} MT Teknoloji. Tüm hakları saklıdır.
              </Typography>
            </Box>
          </Paper>
        </Box>
      </>
    );
  }

  if (submitted) {
    return (
      <>
        <GlobalStyles styles={{
          'html, body': {
            height: '100%',
            overflow: 'auto'
          }
        }} />
        <Box sx={{ 
          p: 4, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '100vh'
        }}>
          <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 'sm', width: '100%' }}>
            <Typography variant="h5" component="h1" gutterBottom color="success.main">
              Teşekkürler!
            </Typography>
            <Box sx={{ my: 4 }}>
              <Typography variant="body1">
                Değerlendirmeniz için teşekkür ederiz. Görüşleriniz bizim için önemli.
              </Typography>
            </Box>
            <Box sx={{ mt: 4 }}>
              <Typography variant="body2" color="text.secondary">
                © {new Date().getFullYear()} MT Teknoloji. Tüm hakları saklıdır.
              </Typography>
            </Box>
          </Paper>
        </Box>
      </>
    );
  }

  return (
    <>
      <GlobalStyles styles={{
        'html, body': {
          height: '100%',
          overflow: 'auto'
        }
      }} />
      <Box sx={{ 
        padding: 2,
        minHeight: '150vh', // Kasıtlı olarak uzun bir sayfa oluşturuyoruz
        position: 'relative',
        overflow: 'auto'
      }}>
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" component="h1" gutterBottom sx={{ textAlign: 'center' }}>
              Memnuniyet Anketi
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
              Öneri ve Görüşleriniz Bizim İçin Değerlidir. Değerlendirmenizde Bize Yardımcı Olduğunuz İçin Teşekkür Ederiz.
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ mb: 4, pr: 1 }}>
              <List>
                {surveys.map((survey, index) => (
                  <ListItem key={survey.id} sx={{ display: 'block', mb: 3, p: 0 }}>
                    <Card variant="outlined" sx={{ mb: 1 }}>
                      <CardContent>
                        <FormControl component="fieldset" fullWidth>
                          <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'medium' }}>
                            {index + 1}. {survey.title}
                          </FormLabel>
                          <RadioGroup
                            value={responses[survey.id] || ''}
                            onChange={(e) => handleResponseChange(survey.id, e.target.value)}
                          >
                            {survey.answers.map((answer, ansIndex) => (
                              <FormControlLabel
                                key={ansIndex}
                                value={answer.text}
                                control={<Radio />}
                                label={answer.text}
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                      </CardContent>
                    </Card>
                    {index < surveys.length - 1 && <Divider />}
                  </ListItem>
                ))}
              </List>
            </Box>
            
            {surveys.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                Size Geri Dönüş Sağlayabilmemiz İçin İletişim Bilgilerinizi Yazabilir, Düşüncelerinizi Bizimle Paylaşabilirsiniz.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Sizinle İletişime Geçelim mi? Görüşlerinizi Bizimle Paylaşın! (maksimum 200 karakter)"
                  value={feedback}
                  onChange={handleFeedbackChange}
                  variant="outlined"
                  inputProps={{ maxLength: 200 }}
                  helperText={`${feedback.length}/200 karakter`}
                />
              </Box>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={submitting}
                sx={{ px: 4, py: 1.5 }}
              >
                {submitting ? (
                  <>
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    Gönderiliyor...
                  </>
                ) : (
                  'Anketi Gönder'
                )}
              </Button>
            </Box>
          </Paper>
          
          <Box sx={{ textAlign: 'center', mt: 3, mb: 10 }}>
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} MT Teknoloji. Tüm hakları saklıdır.
            </Typography>
          </Box>
          
          {/* Sayfanın sonunda fazladan boşluk */}
          <Box sx={{ height: '300px' }} />
        </Container>
      </Box>
    </>
  );
};

export default PublicSurvey; 